'use client';

import {
  Box, Typography, Zoom, Tooltip,
} from '@mui/material';
import { usePathname } from 'next/navigation';
import { sendGTMEvent } from '@next/third-parties/google';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CopyAllIcon from '@mui/icons-material/CopyAll';
import { useMemo } from 'react';
import useCopyContacts from '@/hooks/useCopyContacts';
import { colorVariables } from '@/constants';
import { Vendor } from '@/lib/types/interfaces/Vendor';
// import { isMacintosh } from '@/lib/utils/functions';

interface Props {
  service: Vendor
}

const ContactWidget = ({ service }: Props) => {
  const {
    contacts,
    handleEmail,
    handleCopyUrl,
    handleCopyNumber,
    handlePhoneNumber,
  } = useCopyContacts({ service });

  const pathname = usePathname();

  const itemWrapper = {
    display: 'flex',
    alignItems: 'center',
    paddingX: 3,
    fontWeight: 400,
    cursor: 'pointer',
  };
  const renderLink = useMemo(() => {
    const businessName = service.business_name.replace(/[^a-zA-Z ]/g, '');

    // return encodeURI(`mailto:${service.email}?subject=Inquiry Regarding ${service.business_name} Information&body=Hi ${businessName} \r I found your company through propjump.com and was looking to get more information on your services.`);
    // if (isMacintosh()) {
    // }
    return `mailto:${service.email}?subject=Inquiry Regarding ${service.business_name} Information&body=Hi ${businessName} %0D%0A I found your company through propjump.com and was looking to get more information on your services.`;
  }, []);

  const handleCopyEmail = () => {
    sendGTMEvent({
      url: pathname,
      event: 'click_on_service_provider_email',
      service_provider_name: service.business_name,
    });
  };

  return (
    <Box
      marginTop={2}
      display={{ xs: 'none', lg: 'flex' }}
      color={colorVariables.primaryColor}
    >
      {service.phone_number ? (
        <Box sx={{
          ...itemWrapper,
          borderRight: `1px solid ${colorVariables.separatorColor}`,
          paddingLeft: 0,
        }}
        >
          <PhoneIcon sx={{ marginRight: 1 }} />
          {contacts.isPhoneNumber ? (
            <Zoom in={contacts.isPhoneNumber}>
              <Typography
                variant='body1'
                component='div'
                sx={{ color: 'initial', display: 'flex' }}
              >
                {service.phone_number}
                <Tooltip
                  title='Copied!'
                  open={contacts.isShowTooltip}
                >
                  <CopyAllIcon
                    sx={{ marginLeft: 1 }}
                    onClick={() => handleCopyNumber(service.phone_number)}
                  />
                </Tooltip>
              </Typography>
            </Zoom>
          ) : (
            <Typography
              variant='body1'
              component='div'
              onClick={handlePhoneNumber}
            >
              View Phone Number
            </Typography>
          )}
        </Box>
      ) : null}
      {service.email ? (
        <Box sx={{
          ...itemWrapper,
          ...(!service.phone_number && { paddingLeft: 0 }),
          borderRight: `1px solid ${colorVariables.separatorColor}`,
        }}
        >
          <EmailIcon sx={{ marginRight: 1 }} />
          {contacts.isShowEmail ? (
            <Zoom in={contacts.isShowEmail}>
              <a
                href={renderLink}
                target='_blank'
                rel='noreferrer'
                style={{ color: 'initial' }}
                onClick={handleCopyEmail}
              >
                {service.email}
              </a>
            </Zoom>
          ) : (
            <Typography
              variant='body1'
              component='div'
              onClick={handleEmail}
            >
              View Email
            </Typography>
          )}
        </Box>
      ) : null}
      <Typography
        variant='body1'
        component='div'
        sx={{
          ...itemWrapper,
          ...(!service.phone_number && !service.email && { paddingLeft: 0 }),
          color: contacts.isCopyUrl ? colorVariables.textDescription : colorVariables.primaryColor,
        }}
        onClick={handleCopyUrl}
      >
        <ContentCopyIcon sx={{ marginRight: 1 }} />
        {contacts.isCopyUrl ? 'Copied!' : 'Copy URL'}
      </Typography>
    </Box>
  );
};

export default ContactWidget;
